import React, { FC, useEffect, useRef, useState } from 'react';

import { Button, HierarchyList } from 'common/components';
import { ApiStatus, ButtonVariant, WhatsNewField } from 'common/enums';
import { FieldError, FileDetails } from 'common/models';
import {
    ACTION_ITEMS,
    NAME_MAX_LENGTH,
    NAME_INVALID_CHAR_ERROR,
    NAME_EMPTY_ERROR,
    DESCRIPTION_MAX_LENGTH_ERROR,
    DESCRIPTION_INVALID_CHAR_ERROR,
    DESCRIPTION_MAX_LENGTH,
    FILE_COUNT_MAX,
    NAME_MAX_LENGTH_ERROR,
    FILE_TYPE_ERROR,
    FILE_COUNT_ERROR,
    INVALID_URL,
    MAIN_ROUTES,
    NO_VIDEO_ERROR,
    NO_IMAGE_ERROR,
    ARTICLE_EMPTY_ERROR,
    APPLICATION_EMPTY_ERROR
} from 'common/config';
import { addValidationError, removeValidationError } from 'common/utils/error';

import './AddWhatsNew.scss';
import RadioButton from 'common/components/base/radio-button';
import RadioButtonGroup from 'common/components/base/radio-button-group';
import VideoImageForm from './add-whats-new-tabs/VideoImageForm';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'common/redux/core/root.reducer';
import {
    getSelectedFiles,
    getUploadingFiles,
    getFieldErrors,
    getHierarchicalDataFn,
    getHierarchicalDataValue,
    getWhatsNewResponse,
    getWhatsNewIndicaotrSaveResponse,
    getUploadedStatus,
    getWhatsNewLinkSaveStatus,
    getSelectedIndicatorsForAdd
} from 'modules/configuration/redux/configuration.selector';
import { configurationActions } from 'modules/configuration/redux';
import { getNewlyAddedIndicator, getRemovedIndicator } from 'common/utils/data';
import { useLocation, useNavigate } from 'react-router-dom';
import { isAllowedFileType } from 'common/utils/file';
import AppLinkForm from './add-whats-new-tabs/AppLinkForm';
import { getHierarchyCheckboxListColDefs } from 'modules/configuration/config';
import {
    ConfigurationTypes,
    WhatsNewLinkFieldType,
    WhatsNewLinkType
} from 'modules/configuration/enums/configuration.enum';
import {
    DEFAULT_CONFIGURATION_ID,
    defaultPageRequest
} from 'modules/configuration/constants/add-new-indicator';
import { PagedRequest } from 'common/models/data/pagedRequest.model';
import { WhatsNewIndicatorSaveRequest } from 'modules/configuration/models/configuration.model';
import { MAX_ALLOWED_FREQUENTLY_USED_INDICATORS } from 'common/config/configuration.module.config';

const AddWhatsNew: FC = () => {
    const [name, setName] = useState('');
    const [sortOrder, setSortOrder] = useState('');
    const [description, setDescription] = useState('');
    const [link, setLink] = useState('');
    const [radioButtonSelected, setRadioButton] = useState<WhatsNewLinkFieldType>(
        WhatsNewLinkFieldType.Video
    );
    const fieldErrors = useSelector((state: RootState) => getFieldErrors(state));
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const uploadingFiles = useSelector((state: RootState) => getUploadingFiles(state));
    const [uploadError, setUploadError] = useState<string>('');
    const selectedFiles = useSelector((state: RootState) => getSelectedFiles(state));
    const updateFieldErrors = (errors: Array<FieldError>) => {
        dispatch(configurationActions.setFieldErrors(errors));
    };
    const location = useLocation();
    const [isEditMode, setEditMode] = useState(false);
    const [searchVal, setSearchVal] = useState<string>('');
    const hierarchicalData = useSelector((state: RootState) => getHierarchicalDataFn(state));
    const [title, setTitle] = useState('Create new');
    const pagedRequest = useRef<PagedRequest>(defaultPageRequest);

    const hierarchicalDataResponse = useSelector((state: RootState) =>
        getHierarchicalDataValue(state)
    );
    const whatsNewResponse = useSelector((state: RootState) => getWhatsNewResponse(state));
    const indicatorSaveResponse = useSelector((state: RootState) =>
        getWhatsNewIndicaotrSaveResponse(state)
    );
    const fileUploadStatus = useSelector(getUploadedStatus);
    const whatsNewLinkSaveStatus = useSelector(getWhatsNewLinkSaveStatus);
    const selectedIndicators = useSelector((state: RootState) =>
        getSelectedIndicatorsForAdd(state)
    );

    const isIndicatorSaveSuccess = indicatorSaveResponse?.status === ApiStatus.SUCCESS;

    useEffect(() => {
        if (isIndicatorSaveSuccess) {
            navigate(MAIN_ROUTES.CONFIGURATION_WHATS_NEW);
            return () => {
                dispatch(configurationActions.resetSaveWhatsNewIndicator());
            };
        }
    }, [isIndicatorSaveSuccess]);

    // useEffect(() => {
    //     return () => {
    //         handleCancel();
    //     };
    // }, []);

    useEffect(() => {
        if (fileUploadStatus.status && fileUploadStatus.status === ApiStatus.SUCCESS) {
            navigate(MAIN_ROUTES.CONFIGURATION_WHATS_NEW);
            return () => {
                dispatch(configurationActions.resetUploadStatus());
            };
        }
    }, [dispatch, fileUploadStatus.status, navigate]);

    useEffect(() => {
        if (whatsNewLinkSaveStatus.status && whatsNewLinkSaveStatus.status === ApiStatus.SUCCESS) {
            navigate(MAIN_ROUTES.CONFIGURATION_WHATS_NEW);
            return () => {
                dispatch(configurationActions.resetWhatsNewLinkStatus());
            };
        }
    }, [dispatch, whatsNewLinkSaveStatus.status, navigate]);

    useEffect(() => {
        if (location && location.state && location.state.data) {
            setTitle('Edit');
            const data = location.state.data;
            switch (data.whatsNewTypeId) {
                case WhatsNewLinkType.ArticleLink:
                    setRadioButton(WhatsNewLinkFieldType.Article);
                    setName(data.name);
                    setSortOrder(data.order);
                    setLink(data.link);
                    setEditMode(true);
                    break;
                case WhatsNewLinkType.ApplicationLink:
                    setRadioButton(WhatsNewLinkFieldType.Application);
                    setEditMode(true);
                    setName(data.name);
                    setSortOrder(data.order);
                    setLink(data.link);
                    break;
                case WhatsNewLinkType.Video:
                    setRadioButton(WhatsNewLinkFieldType.Video);
                    setEditMode(true);
                    setName(data.name);
                    setSortOrder(data.order);
                    setDescription(data.description);
                    setLink(data.link);
                    break;
                case WhatsNewLinkType.Image:
                    setRadioButton(WhatsNewLinkFieldType.Image);
                    setEditMode(true);
                    setName(data.name);
                    setSortOrder(data.order);
                    setDescription(data.description);
                    setLink(data.link);
                    break;
                case WhatsNewLinkType.Indicator:
                    setRadioButton(WhatsNewLinkFieldType.Indicator);
                    setEditMode(true);
                    break;
            }
        } else {
            setName('');
            setSortOrder('');
            setDescription('');
            setLink('');
            updateFieldErrors([]);
            setUploadError('');
            dispatch(configurationActions.setSelectedFiles([]));
        }
    }, []);

    useEffect(() => {
        if (radioButtonSelected === WhatsNewLinkFieldType.Indicator)
            dispatch(configurationActions.getCategories(pagedRequest.current));
    }, [dispatch, radioButtonSelected]);
    const handleExpansionToggleCallback = (rowData: any) => {
        dispatch(
            configurationActions.toggleHierarchicalDataExpansion({
                data: rowData,
                configurationType: ConfigurationTypes.WhatsNew
            })
        );
    };
    const getVideoImageValidationStatus = () => {
        if (!isEditMode && selectedFiles.length === 0) {
            return true;
        } else if (isEditMode && link === '' && selectedFiles?.length === 0) {
            return true;
        } else if (isEditMode && link) {
            return false;
        }
        return false;
    };
    const handleAdd = () => {
        if (radioButtonSelected !== WhatsNewLinkFieldType.Indicator) {
            let errors = fieldErrors;
            if (!name.trim()) {
                errors = addValidationError(errors, WhatsNewField.Name, NAME_EMPTY_ERROR);
            }
            if (getVideoImageValidationStatus()) {
                if (radioButtonSelected === WhatsNewLinkFieldType.Video)
                    errors = addValidationError(errors, WhatsNewField.Video, NO_VIDEO_ERROR);
                else if (radioButtonSelected === WhatsNewLinkFieldType.Image)
                    errors = addValidationError(errors, WhatsNewField.Image, NO_IMAGE_ERROR);
            }
            if (!link.trim()) {
                if (radioButtonSelected === WhatsNewLinkFieldType.Article)
                    errors = addValidationError(errors, WhatsNewField.Link, ARTICLE_EMPTY_ERROR);
                else if (radioButtonSelected === WhatsNewLinkFieldType.Application)
                    errors = addValidationError(
                        errors,
                        WhatsNewField.Link,
                        APPLICATION_EMPTY_ERROR
                    );
            }
            if (errors.length === 0) {
                setName('');
                setSortOrder('');
                setDescription('');
                setLink('');
                switch (radioButtonSelected) {
                    case WhatsNewLinkFieldType.Video:
                    case WhatsNewLinkFieldType.Image:
                        {
                            dispatch({
                                type: configurationActions.uploadFile.type,
                                payload: {
                                    whatsNewTypes:
                                        radioButtonSelected === WhatsNewLinkFieldType.Video
                                            ? WhatsNewLinkType.Video
                                            : WhatsNewLinkType.Image,
                                    files: selectedFiles,
                                    name: name,
                                    description: description,
                                    id: isEditMode
                                        ? location.state.data.id
                                        : DEFAULT_CONFIGURATION_ID,
                                    existingFileReplaced: !(isEditMode && link)
                                }
                            });
                        }
                        break;
                    case WhatsNewLinkFieldType.Article:
                    case WhatsNewLinkFieldType.Application:
                        {
                            dispatch({
                                type: configurationActions.saveWhatsNewLink.type,
                                payload: {
                                    id: isEditMode
                                        ? location.state.data.id
                                        : DEFAULT_CONFIGURATION_ID,
                                    name: name,
                                    link: link,
                                    whatsNewTypes:
                                        radioButtonSelected === WhatsNewLinkFieldType.Article
                                            ? WhatsNewLinkType.ArticleLink
                                            : WhatsNewLinkType.ApplicationLink
                                }
                            });
                        }
                        break;

                    default:
                        break;
                }
            }
            updateFieldErrors(errors);
        } else {
            handleIndicatorAdd();
        }
    };

    const handleIndicatorAdd = () => {
        const indicatorList: string[] = [];
        whatsNewResponse.data?.forEach((item: any) => {
            if (item?.indicatorId) {
                indicatorList.push(item.indicatorId);
            }
        });
        const newlyAddedIndicator = getNewlyAddedIndicator(hierarchicalDataResponse, indicatorList);
        const removedIndicator = getRemovedIndicator(hierarchicalDataResponse, indicatorList);
        if (newlyAddedIndicator?.length > 0 || removedIndicator?.length > 0) {
            const apiPayload: WhatsNewIndicatorSaveRequest<WhatsNewLinkType> = {
                whatsNewTypes: WhatsNewLinkType.Indicator,
                addIndicators: newlyAddedIndicator,
                removeIndicators: removedIndicator
            };
            dispatch(configurationActions.saveWhatsNewIndicator(apiPayload));
        }
    };

    const getAllowedCount = () => {
        return MAX_ALLOWED_FREQUENTLY_USED_INDICATORS - (whatsNewResponse.data || []).length;
    };

    const disableSave = () => {
        if (radioButtonSelected === WhatsNewLinkFieldType.Indicator) {
            return selectedIndicators?.length > getAllowedCount() || fieldErrors.length != 0;
        } else {
            return fieldErrors.length != 0;
        }
    };

    const handleDescription = (event: any) => {
        let errors = removeValidationError(fieldErrors, WhatsNewField.Description);
        const reg = new RegExp(/^[a-zA-Z0-9 ]*$/);
        if (event.target.value.length > DESCRIPTION_MAX_LENGTH) {
            errors = addValidationError(
                errors,
                WhatsNewField.Description,
                DESCRIPTION_MAX_LENGTH_ERROR
            );
        }
        if (!reg.test(event.target.value)) {
            errors = addValidationError(
                errors,
                WhatsNewField.Description,
                DESCRIPTION_INVALID_CHAR_ERROR
            );
        }

        setDescription(event.target.value);
        updateFieldErrors(errors);
    };

    const handleNameChange = (event: any) => {
        let errors = removeValidationError(fieldErrors, WhatsNewField.Name);
        const reg = new RegExp(/^[a-zA-Z0-9 ]*$/);
        if (event.target.value.length > NAME_MAX_LENGTH) {
            errors = addValidationError(errors, WhatsNewField.Name, NAME_MAX_LENGTH_ERROR);
        }
        if (!reg.test(event.target.value)) {
            errors = addValidationError(errors, WhatsNewField.Name, NAME_INVALID_CHAR_ERROR);
        }
        setName(event.target.value);
        updateFieldErrors(errors);
    };

    const handleLinkChage = (event: any) => {
        setLink(event.target.value);
    };

    const validateLink = () => {
        let errors = removeValidationError(fieldErrors, WhatsNewField.Link);
        const reg = new RegExp(
            /^(http(s):\/\/.)[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/
        );
        if (link.trim().length !== 0 && !reg.test(link)) {
            errors = addValidationError(errors, WhatsNewField.Link, INVALID_URL);
        }

        updateFieldErrors(errors);
    };

    const handleSortOrderChange = (event: any) => {
        setSortOrder(event.target.value);
    };

    const handleChange = (event: any) => {
        setRadioButton(event.target.value);
        setName('');
        setSortOrder('');
        setDescription('');
        setLink('');
        updateFieldErrors([]);
        setUploadError('');
        dispatch(configurationActions.setSelectedFiles([]));
        dispatch(configurationActions.clearCheckboxSelected());
    };

    const handleSelectedFileRemove = (file: FileDetails) => () => {
        const updatedFiles = selectedFiles
            ? selectedFiles.filter((item: FileDetails) => file.name !== item.name)
            : [];
        dispatch(configurationActions.setSelectedFiles(updatedFiles));
    };

    const removeExistingFile = () => {
        setLink('');
    };

    const handleFileDrop = (files: Array<FileDetails>) => {
        let isAllowedType = true;
        const errors = removeValidationError(
            fieldErrors,
            radioButtonSelected === WhatsNewLinkFieldType.Image
                ? WhatsNewField.Image
                : WhatsNewField.Video
        );
        setUploadError('');
        dispatch(configurationActions.setSelectedFiles([]));

        if (files.length > FILE_COUNT_MAX) {
            setUploadError(FILE_COUNT_ERROR);
        } else {
            const newFiles = files.map((item: FileDetails) => {
                item.id = DEFAULT_CONFIGURATION_ID;
                if (radioButtonSelected === WhatsNewLinkFieldType.Image)
                    isAllowedType = isAllowedFileType(item, /(\.png|\.jpg|\.jpeg|\.gif|\.bmp)$/i);
                else isAllowedType = isAllowedFileType(item, /(\.mp4)$/i);
                return item;
            });
            if (isAllowedType) {
                updateFieldErrors(errors);
                dispatch(configurationActions.setSelectedFiles(newFiles));
            } else {
                setUploadError(FILE_TYPE_ERROR);
            }
        }
    };

    const handleCheckBoxClicked = (isSelected: boolean, data: any) => {
        dispatch(configurationActions.setCheckboxSelected({ isSelected: false, data }));
    };

    const handleCancel = () => {
        switch (radioButtonSelected) {
            case WhatsNewLinkFieldType.Image:
                if (selectedFiles.length > 0) dispatch(configurationActions.setSelectedFiles([]));
                break;
            case WhatsNewLinkFieldType.Video:
                if (selectedFiles.length > 0) dispatch(configurationActions.setSelectedFiles([]));
                break;

            default:
                break;
        }
        dispatch(configurationActions.clearCheckboxSelected());
        navigate(MAIN_ROUTES.CONFIGURATION_WHATS_NEW);
        updateFieldErrors([]);
    };

    return (
        <div className="add-whats-new-modal">
            <div className="seperator">
                <span className="whats-new-add-heading">{`${title} - What's new`}</span>
            </div>
            <div className="add-whats-new-modal-dialog">
                <div className="whats-new-add-header">
                    <p className="header-text">{'Select an option to proceed'}</p>
                </div>
                <div className="whats-new-add-body">
                    <div className="whats-new-body-header">
                        <RadioButtonGroup className="whats-new-radio-button-group">
                            <RadioButton
                                name="radioButton"
                                id={WhatsNewLinkFieldType.Video}
                                value={WhatsNewLinkFieldType.Video}
                                className="whats-new-radio-button"
                                checked={radioButtonSelected === WhatsNewLinkFieldType.Video}
                                disabled={
                                    isEditMode &&
                                    radioButtonSelected !== WhatsNewLinkFieldType.Video
                                }
                                onChange={(event) => handleChange(event)}>
                                Video
                            </RadioButton>
                            <RadioButton
                                name="radioButton"
                                id={WhatsNewLinkFieldType.Indicator}
                                value={WhatsNewLinkFieldType.Indicator}
                                className="whats-new-radio-button"
                                checked={radioButtonSelected === WhatsNewLinkFieldType.Indicator}
                                disabled={
                                    isEditMode &&
                                    radioButtonSelected !== WhatsNewLinkFieldType.Indicator
                                }
                                onChange={(event) => handleChange(event)}>
                                Indicator
                            </RadioButton>
                            <RadioButton
                                name="radioButton"
                                id={WhatsNewLinkFieldType.Article}
                                value={WhatsNewLinkFieldType.Article}
                                className="whats-new-radio-button"
                                disabled={
                                    isEditMode &&
                                    radioButtonSelected !== WhatsNewLinkFieldType.Article
                                }
                                checked={radioButtonSelected === WhatsNewLinkFieldType.Article}
                                onChange={(event) => handleChange(event)}>
                                Article Link
                            </RadioButton>
                            <RadioButton
                                name="radioButton"
                                id={WhatsNewLinkFieldType.Application}
                                value={WhatsNewLinkFieldType.Application}
                                className="whats-new-radio-button"
                                disabled={
                                    isEditMode &&
                                    radioButtonSelected !== WhatsNewLinkFieldType.Application
                                }
                                checked={radioButtonSelected === WhatsNewLinkFieldType.Application}
                                onChange={(event) => handleChange(event)}>
                                Application Link
                            </RadioButton>
                            <RadioButton
                                name="radioButton"
                                id={WhatsNewLinkFieldType.Image}
                                value={WhatsNewLinkFieldType.Image}
                                className="whats-new-radio-button"
                                disabled={
                                    isEditMode &&
                                    radioButtonSelected !== WhatsNewLinkFieldType.Image
                                }
                                checked={radioButtonSelected === WhatsNewLinkFieldType.Image}
                                onChange={(event) => handleChange(event)}>
                                Image
                            </RadioButton>
                        </RadioButtonGroup>
                    </div>
                    {radioButtonSelected === WhatsNewLinkFieldType.Video ||
                    radioButtonSelected === WhatsNewLinkFieldType.Image ? (
                        <VideoImageForm
                            fieldErrors={fieldErrors}
                            name={name}
                            handleNameChange={handleNameChange}
                            sortOrder={sortOrder}
                            handleSortOrderChange={handleSortOrderChange}
                            description={description}
                            handleDescription={handleDescription}
                            uploadingFiles={uploadingFiles}
                            selectedFiles={selectedFiles}
                            errorMessage={uploadError}
                            onSelectedFileRemove={handleSelectedFileRemove}
                            onDrop={handleFileDrop}
                            radioButtonSelected={radioButtonSelected}
                            isEditMode={isEditMode}
                            fileName={link}
                            removeExistingFile={removeExistingFile}
                        />
                    ) : radioButtonSelected === WhatsNewLinkFieldType.Article ||
                      radioButtonSelected === WhatsNewLinkFieldType.Application ? (
                        <AppLinkForm
                            fieldErrors={fieldErrors}
                            name={name}
                            handleNameChange={handleNameChange}
                            sortOrder={sortOrder}
                            handleSortOrderChange={handleSortOrderChange}
                            link={link}
                            handleLink={handleLinkChage}
                            validateLink={validateLink}
                            radioButtonSelected={radioButtonSelected}
                        />
                    ) : radioButtonSelected === WhatsNewLinkFieldType.Indicator ? (
                        <HierarchyList
                            isNoDataVisible={false}
                            data={hierarchicalData.data?.data ?? []}
                            selectorType={'checkbox'}
                            columnDefs={getHierarchyCheckboxListColDefs(
                                true,
                                handleCheckBoxClicked,
                                handleExpansionToggleCallback
                            )}
                        />
                    ) : (
                        ''
                    )}
                </div>
                <div className="whats-new-add-footer">
                    <Button
                        className="footer-button"
                        variant={ButtonVariant.Secondary}
                        onClick={handleCancel}>
                        {ACTION_ITEMS.CANCEL_BUTTON}
                    </Button>
                    <Button
                        className="footer-button"
                        variant={ButtonVariant.Primary}
                        disabled={disableSave()}
                        onClick={handleAdd}>
                        {ACTION_ITEMS.SAVE_BUTTON}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default AddWhatsNew;
